import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { LogoMark } from "./Logo";
import { MaskedWords } from "./Motion";
import { BrandMarquee } from "./Marquee";

const HEADLINE = "Connecting brands and factories with the world\u2019s biggest off-price retailers.";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const markY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 60, damping: 18 });
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      className="relative hero-band grain overflow-hidden pt-36 sm:pt-40 lg:pt-32 lg:min-h-[100svh] flex flex-col"
      data-testid="hero-section"
    >
      <motion.div
        className="pointer-events-none absolute hidden sm:block sm:right-[-10%] sm:top-32 lg:right-[5%] lg:top-[15%] text-[var(--ink)] opacity-[0.06] lg:opacity-[0.075]"
        style={{ y: markY, rotateX: rotX, rotateY: rotY, transformPerspective: 900 }}
        initial={{ scale: 0.92 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
        aria-hidden="true"
      >
        <LogoMark className="h-[420px] lg:h-[min(500px,56vh)] w-auto" stroke={0.7} />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative container-x flex flex-1 flex-col justify-center pb-20 lg:pb-20">
        <motion.p className="eyebrow mb-8 lg:mb-10 flex items-center gap-4 text-[var(--ink-soft)]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }}>
          <span className="inline-block h-px w-8 bg-current" />
          1818 Brands LLC — United States
        </motion.p>
        <h1 className="font-serif-display display-xl text-[2.6rem] sm:text-[3.75rem] lg:text-[4.75rem] xl:text-[5.25rem] leading-[1.03] max-w-[19ch]" data-testid="hero-headline">
          <MaskedWords text={HEADLINE} delay={0.25} stagger={0.055} />
        </h1>
        <motion.div
          className="mt-10 lg:mt-12 flex flex-wrap items-center gap-x-8 gap-y-4"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link
            to="/contact"
            className="group inline-flex h-12 items-center gap-3 rounded-full bg-[var(--ink)] px-7 text-sm font-medium text-[var(--paper)] transition-transform duration-500 hover:-translate-y-0.5"
            data-testid="hero-contact-button"
          >
            Get in touch
            <ArrowDownRight className="h-4 w-4 transition-transform duration-500 group-hover:-rotate-90" strokeWidth={1.5} />
          </Link>
          <Link to="/services" className="link-underline pb-1 text-sm font-medium" data-testid="hero-services-link">
            What we do
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        className="relative border-t border-[var(--line)] bg-[rgba(244,244,241,0.55)] backdrop-blur-[2px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1.2 }}
      >
        <div className="container-x pt-6 lg:pt-7">
          <p className="eyebrow text-[var(--ink-soft)]">Brands we source &amp; distribute</p>
        </div>
        <BrandMarquee />
      </motion.div>
    </section>
  );
};
